import { pb } from "./pocketbase.js";
import { financeiroIntegracaoService } from "./financeiroIntegracaoService.js";

// Helper to convert time "HH:MM" to minutes from midnight
export const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const parts = timeStr.split(":");
  if (parts.length < 2) return 0;
  return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
};

// Helper to convert minutes to "HH:MM"
export const minutesToTime = (totalMinutes) => {
  const isNegative = totalMinutes < 0;
  const absMinutes = Math.abs(totalMinutes);
  const hours = Math.floor(absMinutes / 60);
  const minutes = absMinutes % 60;
  const sign = isNegative ? "-" : "";
  return `${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

export const colaboradoresService = {
  // --- 1. PARSER DE PLANILHA DO RELÓGIO PONTO ---
  async parseTimeClockFile(file) {
    const XLSX = await import("xlsx");
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const bstr = e.target.result;
          const workbook = XLSX.read(bstr, { type: "binary" });
          
          // Find target sheet
          const sheetName = workbook.SheetNames.find(name => 
            name.toLowerCase().includes("registro de presenca") || 
            name.toLowerCase().includes("registo de comparec")
          );
          
          if (!sheetName) {
            return reject(new Error("Planilha de registros ('Registro de Presenca' ou 'Registo de comparec.') não encontrada."));
          }

          const worksheet = workbook.Sheets[sheetName];
          const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          // Detect Year and Month
          let year = new Date().getFullYear();
          let month = new Date().getMonth() + 1;
          const dateRegex = /(\d{4})[-/](\d{2})[-/](\d{2})/;
          
          for (let r = 0; r < Math.min(10, data.length); r++) {
            const row = data[r];
            if (!row) continue;
            const rowStr = JSON.stringify(row);
            const match = rowStr.match(dateRegex);
            if (match) {
              year = parseInt(match[1], 10);
              month = parseInt(match[2], 10);
              break;
            }
          }

          // Find Days Header Row (contains numbers 1, 2, 3...)
          let daysHeader = null;
          for (let r = 0; r < data.length; r++) {
            const row = data[r];
            if (row && row.includes(1) && row.includes(2) && row.includes(3)) {
              daysHeader = row;
              break;
            }
          }

          if (!daysHeader) {
            return reject(new Error("Não foi possível identificar a linha com os dias (1, 2, 3...) na planilha."));
          }

          const parsedEmployees = [];
          
          for (let r = 0; r < data.length; r++) {
            const row = data[r];
            if (!row) continue;

            const rowStr = JSON.stringify(row);
            const isUserInfoRow = rowStr.includes("Namero I：") || rowStr.includes("Tra. No. :");
            
            if (isUserInfoRow) {
              // Extract ID
              const employeeId = row[2] ? String(row[2]).trim() : "";
              
              // Extract Name
              let name = "";
              const nameLabelIdx = row.findIndex(cell => 
                cell && typeof cell === "string" && cell.toLowerCase().includes("nome")
              );
              if (nameLabelIdx !== -1) {
                for (let i = nameLabelIdx + 1; i < row.length; i++) {
                  if (row[i] && String(row[i]).trim()) {
                    name = String(row[i]).trim();
                    break;
                  }
                }
              }

              // Extract Dept
              let dept = "";
              const deptLabelIdx = row.findIndex(cell => 
                cell && typeof cell === "string" && (cell.toLowerCase().includes("dept") || cell.toLowerCase().includes("departamento"))
              );
              if (deptLabelIdx !== -1) {
                for (let i = deptLabelIdx + 1; i < row.length; i++) {
                  if (row[i] && String(row[i]).trim()) {
                    dept = String(row[i]).trim();
                    break;
                  }
                }
              }

              // Punches are on the next row
              const punchesRow = data[r + 1] || [];
              const daysPunches = {};

              daysHeader.forEach((dayNum, cIdx) => {
                if (typeof dayNum === "number") {
                  const cellValue = punchesRow[cIdx];
                  let punches = [];
                  if (cellValue && typeof cellValue === "string") {
                    punches = cellValue.split("\n")
                      .map(p => p.trim())
                      .filter(p => p.match(/^\d{2}:\d{2}$/));
                  }
                  daysPunches[dayNum] = punches;
                }
              });

              parsedEmployees.push({
                id: employeeId,
                name,
                dept,
                daysPunches
              });
            }
          }

          resolve({
            year,
            month,
            employees: parsedEmployees
          });
        } catch (err) {
          reject(err);
        }
      };
      
      reader.onerror = (err) => reject(err);
      reader.readAsBinaryString(file);
    });
  },

  // --- 2. CONFIGURAÇÃO DE COLABORADORES (`colaboradores_config`) ---
  async fetchConfigs() {
    try {
      return await pb.collection("colaboradores_config").getFullList({
        expand: "colaborador,cargo,equipe",
        sort: "colaborador.name"
      });
    } catch (err) {
      console.warn("Falha ao buscar colaboradores_config no PocketBase. Usando localStorage fallback.", err);
      const local = localStorage.getItem("colaboradores_config_local");
      return local ? JSON.parse(local) : [];
    }
  },

  async saveConfig(userId, data) {
    const payload = {
      colaborador: userId,
      relogio_nomes: data.relogio_nomes || [],
      data_admissao: data.data_admissao || null,
      tolerancia_minutos: Number(data.tolerancia_minutos ?? 10),
      banco_horas_inicial: Number(data.banco_horas_inicial ?? 0),
      jornada_seg: data.jornada_seg || null,
      jornada_ter: data.jornada_ter || null,
      jornada_qua: data.jornada_qua || null,
      jornada_qui: data.jornada_qui || null,
      jornada_sex: data.jornada_sex || null,
      ativo: !!(data.ativo ?? true),
      nome_completo: data.nome_completo || "",
      cpf: data.cpf || "",
      data_nascimento: data.data_nascimento || "",
      endereco: data.endereco || "",
      banco: data.banco || "",
      agencia: data.agencia || "",
      conta: data.conta || "",
      pix_chave: data.pix_chave || "",
      tamanho_camiseta: data.tamanho_camiseta || "",
      tamanho_calcado: data.tamanho_calcado || "",
      matricula_esocial: data.matricula_esocial || "",
      data_desligamento: data.data_desligamento || null,
      calculo_rescisorio: data.calculo_rescisorio || "",
      salario: Number(data.salario ?? 0),
      dia_pagamento: Number(data.dia_pagamento ?? 5),
      dia_vale: Number(data.dia_vale ?? 20),
      contratante: data.contratante || "",
      cargo: data.cargo || null,
      equipe: data.equipe || null
    };

    // Build FormData to support multi-file uploads (arquivos and arquivos_rescisao)
    const formData = new FormData();
    formData.append("colaborador", userId);
    formData.append("relogio_nomes", JSON.stringify(payload.relogio_nomes));
    formData.append("data_admissao", payload.data_admissao || "");
    formData.append("tolerancia_minutos", String(payload.tolerancia_minutos));
    formData.append("banco_horas_inicial", String(payload.banco_horas_inicial));
    if (payload.jornada_seg) formData.append("jornada_seg", JSON.stringify(payload.jornada_seg));
    if (payload.jornada_ter) formData.append("jornada_ter", JSON.stringify(payload.jornada_ter));
    if (payload.jornada_qua) formData.append("jornada_qua", JSON.stringify(payload.jornada_qua));
    if (payload.jornada_qui) formData.append("jornada_qui", JSON.stringify(payload.jornada_qui));
    if (payload.jornada_sex) formData.append("jornada_sex", JSON.stringify(payload.jornada_sex));
    formData.append("ativo", String(payload.ativo));

    formData.append("nome_completo", payload.nome_completo);
    formData.append("cpf", payload.cpf);
    formData.append("data_nascimento", payload.data_nascimento);
    formData.append("endereco", payload.endereco);
    formData.append("banco", payload.banco);
    formData.append("agencia", payload.agencia);
    formData.append("conta", payload.conta);
    formData.append("pix_chave", payload.pix_chave);
    formData.append("tamanho_camiseta", payload.tamanho_camiseta);
    formData.append("tamanho_calcado", payload.tamanho_calcado);
    formData.append("matricula_esocial", payload.matricula_esocial);
    formData.append("data_desligamento", payload.data_desligamento || "");
    formData.append("calculo_rescisorio", payload.calculo_rescisorio);
    formData.append("salario", String(payload.salario));
    formData.append("dia_pagamento", String(payload.dia_pagamento));
    formData.append("dia_vale", String(payload.dia_vale));
    formData.append("contratante", payload.contratante);
    formData.append("cargo", payload.cargo || "");
    formData.append("equipe", payload.equipe || "");

    // Existing files to keep (passed as string names)
    if (Array.isArray(data.arquivos_existentes)) {
      data.arquivos_existentes.forEach(filename => {
        formData.append("arquivos", filename);
      });
    }
    // New files to upload
    if (Array.isArray(data.arquivos)) {
      data.arquivos.forEach(file => {
        if (file instanceof File) formData.append("arquivos", file);
      });
    }

    if (Array.isArray(data.arquivos_rescisao_existentes)) {
      data.arquivos_rescisao_existentes.forEach(filename => {
        formData.append("arquivos_rescisao", filename);
      });
    }
    if (Array.isArray(data.arquivos_rescisao)) {
      data.arquivos_rescisao.forEach(file => {
        if (file instanceof File) formData.append("arquivos_rescisao", file);
      });
    }

    try {
      let existing = null;
      try {
        const records = await pb.collection("colaboradores_config").getList(1, 1, {
          filter: `colaborador = "${userId}"`
        });
        existing = records.items[0] || null;
      } catch (e) {}

      if (existing) {
        return await pb.collection("colaboradores_config").update(existing.id, formData);
      } else {
        return await pb.collection("colaboradores_config").create(formData);
      }
    } catch (err) {
      console.warn("Falha ao salvar config no PocketBase. Salvando localmente.", err);
      const local = localStorage.getItem("colaboradores_config_local");
      let list = local ? JSON.parse(local) : [];
      const idx = list.findIndex(c => c.colaborador === userId);
      
      const mockRecord = {
        id: idx >= 0 ? list[idx].id : "mock_conf_" + Math.random().toString(36).substr(2, 9),
        ...payload,
        // Mock filenames just to simulate locally
        arquivos: [
          ...(data.arquivos_existentes || []),
          ...(data.arquivos || []).map(f => f.name || "uploaded_file")
        ],
        arquivos_rescisao: [
          ...(data.arquivos_rescisao_existentes || []),
          ...(data.arquivos_rescisao || []).map(f => f.name || "uploaded_rescisao_file")
        ],
        isLocal: true,
        expand: {
          colaborador: { id: userId, name: data.colaboradorName || "Usuário" }
        }
      };

      if (idx >= 0) list[idx] = mockRecord;
      else list.push(mockRecord);
      
      localStorage.setItem("colaboradores_config_local", JSON.stringify(list));
      return mockRecord;
    }
  },

  // --- 3. ESCALAS DE SÁBADO (`colaboradores_escalas`) ---
  async fetchEscalas() {
    try {
      return await pb.collection("colaboradores_escalas").getFullList({
        expand: "colaboradores",
        sort: "-data"
      });
    } catch (err) {
      console.warn("Falha ao buscar colaboradores_escalas no PocketBase. Usando localStorage fallback.", err);
      const local = localStorage.getItem("colaboradores_escalas_local");
      return local ? JSON.parse(local) : [];
    }
  },

  async saveEscala(dataStr, colaboradoresIds, jornada_entrada = "09:00", jornada_saida = "13:00", observacao = "", escalaId = null) {
    const payload = {
      data: dataStr,
      colaboradores: colaboradoresIds,
      jornada_entrada,
      jornada_saida,
      observacao
    };

    try {
      if (escalaId) {
        return await pb.collection("colaboradores_escalas").update(escalaId, payload);
      } else {
        return await pb.collection("colaboradores_escalas").create(payload);
      }
    } catch (err) {
      console.warn("Falha ao salvar escala no PocketBase. Salvando localmente.", err);
      const local = localStorage.getItem("colaboradores_escalas_local");
      let list = local ? JSON.parse(local) : [];
      const idx = escalaId ? list.findIndex(e => e.id === escalaId) : -1;
      
      const mockRecord = {
        id: idx >= 0 ? list[idx].id : (escalaId || "mock_esc_" + Math.random().toString(36).substr(2, 9)),
        ...payload,
        isLocal: true
      };

      if (idx >= 0) list[idx] = mockRecord;
      else list.push(mockRecord);
      
      localStorage.setItem("colaboradores_escalas_local", JSON.stringify(list));
      return mockRecord;
    }
  },

  async deleteEscala(id) {
    try {
      return await pb.collection("colaboradores_escalas").delete(id);
    } catch (err) {
      console.warn("Falha ao deletar escala do PocketBase. Removendo localmente.", err);
      const local = localStorage.getItem("colaboradores_escalas_local");
      if (local) {
        let list = JSON.parse(local);
        list = list.filter(e => e.id !== id);
        localStorage.setItem("colaboradores_escalas_local", JSON.stringify(list));
      }
      return true;
    }
  },

  // --- 4. REGISTROS DE PONTO (`colaboradores_ponto_registros`) ---
  async fetchAllPontoRegistros(colaboradorId) {
    try {
      let filter = "";
      if (colaboradorId) {
        filter = `colaborador = "${colaboradorId}"`;
      }
      return await pb.collection("colaboradores_ponto_registros").getFullList({
        filter: filter || null,
        sort: "data"
      });
    } catch (err) {
      console.warn("Falha ao buscar todos os colaboradores_ponto_registros. Usando localStorage fallback.", err);
      let list = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key.startsWith("colaboradores_ponto_local_")) {
          try {
            const items = JSON.parse(localStorage.getItem(key));
            if (Array.isArray(items)) {
              list.push(...items);
            }
          } catch (e) {}
        }
      }
      if (colaboradorId) {
        return list.filter(r => r.colaborador === colaboradorId);
      }
      return list;
    }
  },

  async fetchPontoRegistros(colaboradorId, mes, ano) {
    const pad = (n) => String(n).padStart(2, "0");
    const startStr = `${ano}-${pad(mes)}-01`;
    const endStr = `${ano}-${pad(mes)}-31`; // Simplificado

    try {
      let filter = `data >= "${startStr}" && data <= "${endStr}"`;
      if (colaboradorId) {
        filter += ` && colaborador = "${colaboradorId}"`;
      }
      return await pb.collection("colaboradores_ponto_registros").getFullList({
        filter,
        sort: "data"
      });
    } catch (err) {
      console.warn("Falha ao buscar colaboradores_ponto_registros. Usando localStorage fallback.", err);
      const localKey = `colaboradores_ponto_local_${mes}_${ano}`;
      const local = localStorage.getItem(localKey);
      const list = local ? JSON.parse(local) : [];
      if (colaboradorId) {
        return list.filter(r => r.colaborador === colaboradorId);
      }
      return list;
    }
  },

  async savePontoRegistro(colaboradorId, dataStr, data) {
    const payload = {
      colaborador: colaboradorId,
      data: dataStr,
      batidas_originais: data.batidas_originais || [],
      batidas_ajustadas: data.batidas_ajustadas || [],
      motivo_ajuste: data.motivo_ajuste || "",
      atraso_minutos: Number(data.atraso_minutos ?? 0),
      hora_extra_minutos: Number(data.hora_extra_minutos ?? 0),
      saldo_minutos: Number(data.saldo_minutos ?? 0),
      abono_minutos: Number(data.abono_minutos ?? 0),
      justificativa_abono: data.justificativa_abono || "",
      compensado: !!data.compensado
    };

    if (data.isBeforeAdmission) {
      payload.atraso_minutos = 0;
      payload.hora_extra_minutos = 0;
      payload.saldo_minutos = 0;
      payload.abono_minutos = 0;
      payload.justificativa_abono = "";
    }

    // Form data upload if medical certificate is present
    let finalPayload = payload;
    let hasFile = false;
    if (data.certificado_medico instanceof File) {
      hasFile = true;
      const formData = new FormData();
      Object.keys(payload).forEach(key => {
        if (Array.isArray(payload[key])) {
          formData.append(key, JSON.stringify(payload[key]));
        } else {
          formData.append(key, payload[key]);
        }
      });
      formData.append("certificado_medico", data.certificado_medico);
      finalPayload = formData;
    }

    try {
      let existing = null;
      try {
        const records = await pb.collection("colaboradores_ponto_registros").getList(1, 1, {
          filter: `colaborador = "${colaboradorId}" && data = "${dataStr}"`
        });
        existing = records.items[0] || null;
      } catch (e) {}

      if (existing) {
        // Se já existiam dados ajustados/abonados e estamos importando a versão original,
        // garantimos preservar os ajustes e abonos feitos pelo gestor.
        if (data.isImportProcess) {
          const updatePayload = {
            batidas_originais: payload.batidas_originais,
            // Preserva o resto que já está no banco caso o usuário já tenha ajustado!
            batidas_ajustadas: existing.batidas_ajustadas?.length ? existing.batidas_ajustadas : payload.batidas_ajustadas,
            motivo_ajuste: existing.motivo_ajuste || payload.motivo_ajuste,
            abono_minutos: existing.abono_minutos ?? payload.abono_minutos,
            justificativa_abono: existing.justificativa_abono || payload.justificativa_abono
          };
          
          // Re-calcular saldo considerando as marcações preferidas (ajustadas se existirem, senão originais)
          const actualPunches = updatePayload.batidas_ajustadas?.length ? updatePayload.batidas_ajustadas : updatePayload.batidas_originais;
          const calcs = this.calcularHorasTrabalho(actualPunches, data.jornada, data.tolerancia, updatePayload.abono_minutos);
          
          updatePayload.atraso_minutos = calcs.atraso;
          updatePayload.hora_extra_minutos = calcs.hora_extra;
          updatePayload.saldo_minutos = calcs.saldo;

          if (data.isBeforeAdmission) {
            updatePayload.atraso_minutos = 0;
            updatePayload.hora_extra_minutos = 0;
            updatePayload.saldo_minutos = 0;
            updatePayload.abono_minutos = 0;
            updatePayload.justificativa_abono = "";
          }

          return await pb.collection("colaboradores_ponto_registros").update(existing.id, updatePayload);
        }

        return await pb.collection("colaboradores_ponto_registros").update(existing.id, finalPayload);
      } else {
        if (data.isImportProcess) {
          const actualPunches = payload.batidas_originais;
          const calcs = this.calcularHorasTrabalho(actualPunches, data.jornada, data.tolerancia, payload.abono_minutos);
          
          payload.atraso_minutos = calcs.atraso;
          payload.hora_extra_minutos = calcs.hora_extra;
          payload.saldo_minutos = calcs.saldo;

          if (data.isBeforeAdmission) {
            payload.atraso_minutos = 0;
            payload.hora_extra_minutos = 0;
            payload.saldo_minutos = 0;
            payload.abono_minutos = 0;
            payload.justificativa_abono = "";
          }
          
          if (finalPayload !== payload && typeof finalPayload.set === "function") {
            finalPayload.set("atraso_minutos", String(payload.atraso_minutos));
            finalPayload.set("hora_extra_minutos", String(payload.hora_extra_minutos));
            finalPayload.set("saldo_minutos", String(payload.saldo_minutos));
            finalPayload.set("abono_minutos", String(payload.abono_minutos));
            finalPayload.set("justificativa_abono", "");
          }
        }
        return await pb.collection("colaboradores_ponto_registros").create(finalPayload);
      }
    } catch (err) {
      console.warn("Falha ao salvar ponto no PocketBase. Salvando localmente.", err);
      // Salva localmente com chave baseada no mes/ano da data
      const dateParts = dataStr.split("-"); // YYYY-MM-DD
      const mesStr = Number(dateParts[1]);
      const anoStr = Number(dateParts[0]);
      
      const localKey = `colaboradores_ponto_local_${mesStr}_${anoStr}`;
      const local = localStorage.getItem(localKey);
      let list = local ? JSON.parse(local) : [];
      const idx = list.findIndex(r => r.colaborador === colaboradorId && r.data === dataStr);

      const mockRecord = {
        id: idx >= 0 ? list[idx].id : "mock_pt_" + Math.random().toString(36).substr(2, 9),
        ...payload,
        certificado_medico: hasFile ? "arquivo_mock_atestado.pdf" : null,
        isLocal: true
      };

      if (idx >= 0) list[idx] = mockRecord;
      else list.push(mockRecord);
      
      localStorage.setItem(localKey, JSON.stringify(list));
      return mockRecord;
    }
  },

  // --- 5. HISTÓRICO DE PAGAMENTOS (`colaboradores_historico_pagamentos`) ---
  async fetchPayments(colaboradorId, mes, ano) {
    try {
      let filters = [];
      if (Number(ano) > 0) {
        filters.push(`ano = ${Number(ano)}`);
      }
      if (Number(mes) > 0) {
        filters.push(`mes = ${Number(mes)}`);
      }
      if (colaboradorId) {
        filters.push(`colaborador = "${colaboradorId}"`);
      }
      const filter = filters.join(" && ");
      return await pb.collection("colaboradores_historico_pagamentos").getFullList({
        filter: filter || null,
        sort: "colaborador.name,-created"
      });
    } catch (err) {
      console.warn("Falha ao buscar colaboradores_historico_pagamentos. Usando localStorage fallback.", err);
      let list = [];
      if (Number(ano) > 0) {
        const localKey = `colaboradores_pagamentos_local_${ano}`;
        const local = localStorage.getItem(localKey);
        list = local ? JSON.parse(local) : [];
      } else {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key.startsWith("colaboradores_pagamentos_local_")) {
            try {
              const items = JSON.parse(localStorage.getItem(key));
              if (Array.isArray(items)) {
                list.push(...items);
              }
            } catch (e) {}
          }
        }
      }
      if (Number(mes) > 0) list = list.filter(p => p.mes === Number(mes));
      if (colaboradorId) list = list.filter(p => p.colaborador === colaboradorId);
      return list;
    }
  },

  async savePayment(colaboradorId, mes, ano, data) {
    const payload = {
      colaborador: colaboradorId,
      mes: Number(mes),
      ano: Number(ano),
      tipo: data.tipo, // 'bonus' | 'desconto' | 'salario' | 'banco_horas_pago'
      valor: Number(data.valor),
      descricao: data.descricao,
      pago: !!data.pago,
      data_pagamento: data.data_pagamento || null
    };

    if (data.minutos_compensados !== undefined) {
      payload.minutos_compensados = Number(data.minutos_compensados);
    }

    let result = null;
    let oldRecord = null;

    if (data.id && !data.id.startsWith("mock_")) {
      try {
        oldRecord = await pb.collection("colaboradores_historico_pagamentos").getOne(data.id);
      } catch (e) {}
    }

    try {
      if (data.id && !data.id.startsWith("mock_")) {
        try {
          result = await pb.collection("colaboradores_historico_pagamentos").update(data.id, payload);
        } catch (err) {
          if (err.status === 400 && String(err.message).toLowerCase().includes("minutos_compensados")) {
            const fallbackPayload = { ...payload };
            delete fallbackPayload.minutos_compensados;
            if (data.minutos_compensados > 0) {
              fallbackPayload.descricao = `[Compensa: ${data.minutos_compensados} min] ${fallbackPayload.descricao}`;
            }
            result = await pb.collection("colaboradores_historico_pagamentos").update(data.id, fallbackPayload);
          } else {
            throw err;
          }
        }
      } else {
        try {
          result = await pb.collection("colaboradores_historico_pagamentos").create(payload);
        } catch (err) {
          if (err.status === 400 && String(err.message).toLowerCase().includes("minutos_compensados")) {
            const fallbackPayload = { ...payload };
            delete fallbackPayload.minutos_compensados;
            if (data.minutos_compensados > 0) {
              fallbackPayload.descricao = `[Compensa: ${data.minutos_compensados} min] ${fallbackPayload.descricao}`;
            }
            result = await pb.collection("colaboradores_historico_pagamentos").create(fallbackPayload);
          } else {
            throw err;
          }
        }
      }

      // Gatilho Financeiro se salvou com sucesso no PocketBase
      if (result) {
        const wasPago = oldRecord ? !!oldRecord.pago : false;
        const isPago = !!result.pago;

        if (!wasPago && isPago) {
          // Dispara despesa para o Financeiro
          try {
            let colName = "Colaborador ID: " + colaboradorId;
            try {
              const col = await pb.collection("colaboradores").getOne(colaboradorId, { fields: "nome" });
              if (col) colName = col.nome;
            } catch (e) {}

            await financeiroIntegracaoService.triggerPendingTransaction({
              origem: "colaboradores_historico_pagamentos",
              origemId: result.id,
              snapshot: result,
              dataSugerida: result.data_pagamento ? result.data_pagamento.split(" ")[0].split("T")[0] : new Date().toISOString().split("T")[0],
              valorSugeridoCentavos: Math.round(Number(result.valor) * 100),
              descricaoSugerida: `Pgto ${result.tipo?.toUpperCase() || "SALÁRIO"} - ${colName} (${result.mes}/${result.ano})`,
              tipoPendencia: "estorno",
            });
          } catch (errFin) {
            console.warn("Erro ao gerar pendência financeira de folha:", errFin);
          }
        } else if (wasPago && !isPago) {
          // Cancelamento de pagamento
          try {
            await financeiroIntegracaoService.triggerCancellation("colaboradores_historico_pagamentos", result.id, pb.authStore.model);
          } catch (errFin) {
            console.warn("Erro ao cancelar pendência de folha de pagamento:", errFin);
          }
        }
      }

      return result;
    } catch (err) {
      console.warn("Falha ao salvar pagamento no PocketBase. Salvando localmente.", err);
      const localKey = `colaboradores_pagamentos_local_${ano}`;
      const local = localStorage.getItem(localKey);
      let list = local ? JSON.parse(local) : [];
      
      const isEdit = data.id && list.some(p => p.id === data.id);
      const mockRecord = {
        id: isEdit ? data.id : "mock_pay_" + Math.random().toString(36).substr(2, 9),
        ...payload,
        isLocal: true
      };

      if (isEdit) {
        const idx = list.findIndex(p => p.id === data.id);
        list[idx] = mockRecord;
      } else {
        list.push(mockRecord);
      }
      
      localStorage.setItem(localKey, JSON.stringify(list));
      return mockRecord;
    }
  },

  async deletePayment(id) {
    try {
      return await pb.collection("colaboradores_historico_pagamentos").delete(id);
    } catch (err) {
      console.warn("Falha ao deletar pagamento no PocketBase. Removendo localmente.", err);
      // We don't have the year easily here, so we scan all possible years in local storage
      for (let y = new Date().getFullYear() - 1; y <= new Date().getFullYear() + 1; y++) {
        const localKey = `colaboradores_pagamentos_local_${y}`;
        const local = localStorage.getItem(localKey);
        if (local) {
          let list = JSON.parse(local);
          if (list.some(p => p.id === id)) {
            list = list.filter(p => p.id !== id);
            localStorage.setItem(localKey, JSON.stringify(list));
            break;
          }
        }
      }
      return true;
    }
  },
  // --- 5.1 CARGOS & EQUIPES ---
  async fetchCargos() {
    try {
      return await pb.collection("colaboradores_cargos").getFullList({
        sort: "nome"
      });
    } catch (err) {
      console.warn("Falha ao buscar cargos no PocketBase. Usando localStorage fallback.", err);
      const local = localStorage.getItem("colaboradores_cargos_local");
      return local ? JSON.parse(local) : [];
    }
  },

  async saveCargo(cargoId, data) {
    const payload = {
      nome: data.nome,
      descricao: data.descricao || "",
      cbo: data.cbo || "",
      setor: data.setor || "",
      contratante: data.contratante || ""
    };
    try {
      if (cargoId) {
        return await pb.collection("colaboradores_cargos").update(cargoId, payload);
      } else {
        return await pb.collection("colaboradores_cargos").create(payload);
      }
    } catch (err) {
      console.warn("Falha ao salvar cargo no PocketBase. Salvando localmente.", err);
      const local = localStorage.getItem("colaboradores_cargos_local");
      let list = local ? JSON.parse(local) : [];
      const isEdit = cargoId && list.some(c => c.id === cargoId);
      const mockRecord = {
        id: isEdit ? cargoId : "mock_cargo_" + Math.random().toString(36).substr(2, 9),
        ...payload,
        isLocal: true
      };
      if (isEdit) {
        const idx = list.findIndex(c => c.id === cargoId);
        list[idx] = mockRecord;
      } else {
        list.push(mockRecord);
      }
      localStorage.setItem("colaboradores_cargos_local", JSON.stringify(list));
      return mockRecord;
    }
  },

  async deleteCargo(id) {
    try {
      return await pb.collection("colaboradores_cargos").delete(id);
    } catch (err) {
      console.warn("Falha ao deletar cargo no PocketBase. Removendo localmente.", err);
      const local = localStorage.getItem("colaboradores_cargos_local");
      if (local) {
        let list = JSON.parse(local);
        list = list.filter(c => c.id !== id);
        localStorage.setItem("colaboradores_cargos_local", JSON.stringify(list));
      }
      return true;
    }
  },

  async fetchEquipes() {
    try {
      return await pb.collection("colaboradores_equipes").getFullList({
        expand: "lider,vice_lider,membros",
        sort: "nome"
      });
    } catch (err) {
      console.warn("Falha ao buscar equipes no PocketBase. Usando localStorage fallback.", err);
      const local = localStorage.getItem("colaboradores_equipes_local");
      return local ? JSON.parse(local) : [];
    }
  },

  async saveEquipe(equipeId, data) {
    const payload = {
      nome: data.nome,
      descricao: data.descricao || "",
      lider: data.lider || null,
      vice_lider: data.vice_lider || null,
      contratante: data.contratante || ""
    };
    if (data.membros !== undefined) {
      payload.membros = data.membros;
    }
    try {
      if (equipeId) {
        return await pb.collection("colaboradores_equipes").update(equipeId, payload);
      } else {
        return await pb.collection("colaboradores_equipes").create(payload);
      }
    } catch (err) {
      console.warn("Falha ao salvar equipe no PocketBase. Salvando localmente.", err);
      const local = localStorage.getItem("colaboradores_equipes_local");
      let list = local ? JSON.parse(local) : [];
      const isEdit = equipeId && list.some(e => e.id === equipeId);
      const mockRecord = {
        id: isEdit ? equipeId : "mock_eq_" + Math.random().toString(36).substr(2, 9),
        ...payload,
        isLocal: true
      };
      if (isEdit) {
        const idx = list.findIndex(e => e.id === equipeId);
        list[idx] = mockRecord;
      } else {
        list.push(mockRecord);
      }
      localStorage.setItem("colaboradores_equipes_local", JSON.stringify(list));
      return mockRecord;
    }
  },

  async deleteEquipe(id) {
    try {
      return await pb.collection("colaboradores_equipes").delete(id);
    } catch (err) {
      console.warn("Falha ao deletar equipe no PocketBase. Removendo localmente.", err);
      const local = localStorage.getItem("colaboradores_equipes_local");
      if (local) {
        let list = JSON.parse(local);
        list = list.filter(e => e.id !== id);
        localStorage.setItem("colaboradores_equipes_local", JSON.stringify(list));
      }
      return true;
    }
  },

  // --- 6. MOTOR DE CÁLCULO DE PRESENÇA (Cálculo de Atraso, Horas Extras, Saldo) ---
  calcularHorasTrabalho(batidas = [], jornada = null, tolerancia = 10, abonoMinutos = 0) {
    // Se não há batidas e não há jornada esperada, o dia é neutro
    const expectedMinutes = jornada ? (jornada.horas_esperadas || 0) : 0;
    if (batidas.length === 0) {
      // Sem batidas em dia útil com jornada esperada -> Falta total
      if (expectedMinutes > 0) {
        const saldo = -expectedMinutes + abonoMinutos;
        return {
          trabalhado: 0,
          atraso: abonoMinutos >= expectedMinutes ? 0 : expectedMinutes - abonoMinutos,
          hora_extra: 0,
          saldo: Math.min(0, saldo) // o saldo de falta total abonada pode chegar a 0
        };
      }
      // Sem batidas em dia de folga/neutro
      return { trabalhado: 0, atraso: 0, hora_extra: 0, saldo: 0 };
    }

    // Calcula tempo trabalhado em minutos
    let workedMinutes = 0;
    
    // Agrupa batidas de duas em duas (Entrada-Saída)
    // Se houver batidas ímpares (ex: 3), a última batida fica sem par (incompleto) e não soma
    for (let i = 0; i < batidas.length - 1; i += 2) {
      const entryVal = batidas[i];
      const exitVal = batidas[i + 1];
      
      const diff = timeToMinutes(exitVal) - timeToMinutes(entryVal);
      if (diff > 0) workedMinutes += diff;
    }

    // Se o dia não tem jornada esperada (ex: sábado sem escala, ou domingo)
    if (expectedMinutes === 0) {
      // Todo o tempo trabalhado é Hora Extra
      const saldo = workedMinutes + abonoMinutos;
      return {
        trabalhado: workedMinutes,
        atraso: 0,
        hora_extra: workedMinutes,
        saldo: saldo
      };
    }

    // Dia útil regular (com jornada esperada)
    // O abono reduz a jornada esperada que o colaborador precisa cumprir
    const effectiveExpectedMinutes = Math.max(0, expectedMinutes - abonoMinutos);
    const rawDiff = workedMinutes - effectiveExpectedMinutes; // saldo real do dia
    
    // Aplicação da tolerância diária
    let saldo = 0;
    let atraso = 0;
    let hora_extra = 0;

    if (Math.abs(rawDiff) <= tolerancia) {
      // Dentro da tolerância, saldo do dia é zero.
      saldo = 0;
    } else {
      // Excedeu a tolerância, computa a diferença cheia.
      saldo = rawDiff;
    }

    if (saldo < 0) {
      atraso = Math.abs(saldo);
    } else {
      hora_extra = saldo;
    }

    return {
      trabalhado: workedMinutes,
      atraso,
      hora_extra,
      saldo
    };
  }
};
